import i18n from '@/utils/i18n';
import {
  buildAccountKey,
  splitAccountKey,
} from '@sales-portal/domain/pricing/blocs/utils/account-key';
import { BulkEditMode } from '@sales-portal/domain/pricing/screens/management/components/pricing-bulk-edit-bottom-bar';
import {
  computeActionEdit,
  computeAddAmountEdit,
  computeExplicitValueEdit,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/quick-action-calculations';
import {
  EditScope,
  GrossMarginEditAction,
  PriceEditAction,
  PriceEditActionType,
  PricingEdit,
  PricingEditField,
  RevertEditAction,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import {
  hasEditablePricing,
  PricingAccountRow,
} from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { isEditAtOriginalBaseline } from '@sales-portal/domain/pricing/screens/management/blocs/utils/pricing-edit-normalization';
import {
  PAGE_SOURCE_BY_EDIT_SCOPE,
  PricingAnalyticEvent,
  pricingManagementAnalytics,
} from '@sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { parseTransientAwareNumber } from '@sales-portal/domain/pricing/screens/management/blocs/utils/numeric-input';
import { useCallback, useMemo, useState } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { usePricingEditsStore } from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store';
import { validatePricingFloor } from '@sales-portal/domain/pricing/screens/management/blocs/utils/pricing-input-validator';
import { VariantPackCostType } from '@/types';

/**
 * Which dimension is held fixed for a bulk edit. The other dimension is the
 * selected set the edit fans out across, identified by a selection key:
 *  - `VariantPack`: fixed item, keys are restaurant UUIDs (item details page).
 *  - `Restaurant`: fixed customer, keys are variant pack UUIDs (customer detail).
 *  - `Pair`: nothing fixed, keys are {@link buildAccountKey}s, so a selection can
 *    span many items and customers (bookmarked page).
 */
export enum BulkEditPivot {
  Pair = 'pair',
  Restaurant = 'restaurant',
  VariantPack = 'variantPack',
}

type BulkQuickActionId =
  | GrossMarginEditAction.AddOnePercent
  | GrossMarginEditAction.SetLastMargin
  | GrossMarginEditAction.SubtractOnePercent
  | PriceEditAction.EndInX9
  | PriceEditAction.RandomRoundUp
  | PriceEditAction.SetLastPrice
  | RevertEditAction.RevertEdit;

export interface BulkQuickActionState {
  disabled: boolean;
  id: BulkQuickActionId;
  onSelect: () => void;
}

interface SelectedRowData {
  effectiveRow: PricingAccountRow;
  key: string;
  originalRow: PricingAccountRow;
}

interface BulkEditingCoreParams {
  currencyCode: string;
  /** Effective (edit-merged) rows, already adapted to the cost-change shape. */
  mergedRows: PricingAccountRow[];
  pivot: BulkEditPivot;
  /** The fixed dimension's UUID. Unused (and omitted) for the `Pair` pivot. */
  pivotUUID?: string;
  /** Table-wide floor settings; omit to use each row's own floor settings. */
  priceFloorEnabled?: boolean;
  priceFloorMode?: VariantPackCostType | null;
  pricingEdits: PricingEdit[];
  /** Original (baseline) rows, already adapted to the cost-change shape. */
  rows: PricingAccountRow[];
  scope: EditScope;
  /** Selection keys, in the shape the pivot dictates. */
  selectedKeys: Set<string>;
}

const keyOfRow = (pivot: BulkEditPivot, row: PricingAccountRow): string => {
  if (pivot === BulkEditPivot.Pair) {
    return buildAccountKey(row);
  }
  return pivot === BulkEditPivot.VariantPack
    ? row.restaurantUUID
    : row.variantPackUUID;
};

const keyOfEdit = (pivot: BulkEditPivot, edit: PricingEdit): string => {
  if (pivot === BulkEditPivot.Pair) {
    return buildAccountKey(edit);
  }
  return pivot === BulkEditPivot.VariantPack
    ? edit.restaurantUUID
    : edit.variantPackUUID;
};

const editMatchesPivot = (
  pivot: BulkEditPivot,
  pivotUUID: string,
  edit: PricingEdit,
): boolean => {
  if (pivot === BulkEditPivot.Pair) {
    return true;
  }
  return pivot === BulkEditPivot.VariantPack
    ? edit.variantPackUUID === pivotUUID
    : edit.restaurantUUID === pivotUUID;
};

const toastNameOf = (pivot: BulkEditPivot, row: PricingAccountRow): string =>
  pivot === BulkEditPivot.Restaurant ? row.variantPackName : row.restaurantName;

const indexByKey = (
  pivot: BulkEditPivot,
  rows: PricingAccountRow[],
): Record<string, PricingAccountRow> =>
  rows.reduce<Record<string, PricingAccountRow>>((acc, row) => {
    acc[keyOfRow(pivot, row)] = row;
    return acc;
  }, {});

/**
 * Shared bulk-edit engine for the pricing tables. The screens that use it are
 * the same engine transposed on the {@link BulkEditPivot}: the item details
 * page fixes a variant pack and edits many restaurants, the customer detail
 * page fixes a restaurant and edits many variant packs, and the bookmarked page
 * fixes neither and edits an arbitrary set of (variant pack, restaurant) pairs.
 * All logic is identical once expressed in pivot/selection-key terms.
 */
export const useBulkEditingCore = ({
  currencyCode,
  mergedRows,
  pivot,
  pivotUUID = '',
  priceFloorEnabled,
  priceFloorMode,
  pricingEdits,
  rows,
  scope,
  selectedKeys,
}: BulkEditingCoreParams) => {
  const [bulkEditField, setBulkEditField] = useState<PricingEditField>(
    PricingEditField.Price,
  );
  const [bulkEditMode, setBulkEditMode] = useState<BulkEditMode>(
    BulkEditMode.Add,
  );
  const [bulkInputValue, setBulkInputValue] = useState('');

  const { showToast } = useExperienceToasts();
  const applyEdits = usePricingEditsStore((state) => state.applyEdits);
  const revertEditsByVariantPack = usePricingEditsStore(
    (state) => state.revertEditsByVariantPack,
  );
  const revertEditsByRestaurant = usePricingEditsStore(
    (state) => state.revertEditsByRestaurant,
  );

  const originalRowsByKey = useMemo(
    () => indexByKey(pivot, rows),
    [pivot, rows],
  );

  const revertKeys = useCallback(
    (keys: string[]) => {
      if (pivot === BulkEditPivot.Pair) {
        // The store reverts per variant pack, so a pair selection is grouped by
        // item before it is handed over.
        const restaurantsByVariantPack = keys.reduce<Record<string, string[]>>(
          (acc, key) => {
            const { restaurantUUID, variantPackUUID } = splitAccountKey(key);
            (acc[variantPackUUID] ??= []).push(restaurantUUID);
            return acc;
          },
          {},
        );
        Object.entries(restaurantsByVariantPack).forEach(
          ([variantPackUUID, restaurantUUIDs]) =>
            revertEditsByVariantPack(variantPackUUID, restaurantUUIDs, scope),
        );
        return;
      }

      if (pivot === BulkEditPivot.VariantPack) {
        revertEditsByVariantPack(pivotUUID, keys, scope);
      } else {
        revertEditsByRestaurant(pivotUUID, keys, scope);
      }
    },
    [
      pivot,
      pivotUUID,
      revertEditsByVariantPack,
      revertEditsByRestaurant,
      scope,
    ],
  );

  const analyticsPageSource = PAGE_SOURCE_BY_EDIT_SCOPE[scope.kind];

  // A biz-org bulk edit can fan out across rows from multiple suppliers, so
  // resolve the affected (deduped) suppliers from the selected rows.
  const analyticsTargets = useCallback(
    (
      keys: string[],
    ): {
      restaurantUUIDs: string[];
      supplierUUIDs: string[];
      variantPackUUIDs: string[];
    } => {
      const supplierUUIDs = Array.from(
        new Set(
          keys
            .map((key) => originalRowsByKey[key]?.supplierUUID)
            .filter((uuid): uuid is string => Boolean(uuid)),
        ),
      );

      if (pivot === BulkEditPivot.Pair) {
        const pairs = keys.map(splitAccountKey);
        return {
          restaurantUUIDs: Array.from(
            new Set(pairs.map(({ restaurantUUID }) => restaurantUUID)),
          ),
          supplierUUIDs,
          variantPackUUIDs: Array.from(
            new Set(pairs.map(({ variantPackUUID }) => variantPackUUID)),
          ),
        };
      }

      const isVariantPackPivot = pivot === BulkEditPivot.VariantPack;
      return {
        restaurantUUIDs: isVariantPackPivot ? keys : [pivotUUID],
        supplierUUIDs,
        variantPackUUIDs: isVariantPackPivot ? [pivotUUID] : keys,
      };
    },
    [originalRowsByKey, pivot, pivotUUID],
  );

  const trackBulkEdit = useCallback(
    (quickAction: string, keys: string[]) => {
      pricingManagementAnalytics.track(PricingAnalyticEvent.BULK_EDIT, {
        pageSource: analyticsPageSource,
        quickAction,
        ...analyticsTargets(keys),
      });
    },
    [analyticsPageSource, analyticsTargets],
  );

  const trackBulkReversion = useCallback(
    (keys: string[]) => {
      pricingManagementAnalytics.track(PricingAnalyticEvent.BULK_REVERSION, {
        pageSource: analyticsPageSource,
        ...analyticsTargets(keys),
      });
    },
    [analyticsPageSource, analyticsTargets],
  );

  const filteredPriceEditMap = useMemo(
    () =>
      pricingEdits.reduce<Record<string, PricingEdit>>((acc, edit) => {
        if (editMatchesPivot(pivot, pivotUUID, edit)) {
          acc[keyOfEdit(pivot, edit)] = edit;
        }
        return acc;
      }, {}),
    [pivot, pivotUUID, pricingEdits],
  );

  const effectiveRowsByKey = useMemo(
    () => indexByKey(pivot, mergedRows),
    [mergedRows, pivot],
  );

  const selectedRowsForBulkEdit = useMemo<SelectedRowData[]>(
    () =>
      [...selectedKeys].reduce<SelectedRowData[]>((acc, key) => {
        const originalRow = originalRowsByKey[key];
        const effectiveRow = effectiveRowsByKey[key];

        // Rows with no price or margin have no cost to price against; they stay
        // selectable for bookmark actions but take no pricing edits.
        if (!originalRow || !effectiveRow || !hasEditablePricing(originalRow)) {
          return acc;
        }

        acc.push({ effectiveRow, key, originalRow });
        return acc;
      }, []),
    [effectiveRowsByKey, originalRowsByKey, selectedKeys],
  );

  const parsedBulkInputValue = useMemo(
    () => parseTransientAwareNumber(bulkInputValue),
    [bulkInputValue],
  );

  const selectedEditedKeys = useMemo(
    () =>
      selectedRowsForBulkEdit.reduce<string[]>((acc, { key }) => {
        if (filteredPriceEditMap[key]) {
          acc.push(key);
        }
        return acc;
      }, []),
    [filteredPriceEditMap, selectedRowsForBulkEdit],
  );

  const applyBulkComputedEdits = useCallback(
    (
      computedEdits: Array<{
        edit: PricingEdit | null;
        key: string;
        originalRow: PricingAccountRow;
      }>,
    ): boolean => {
      const editsToApply: PricingEdit[] = [];
      const keysToRevert: string[] = [];

      for (const { edit, key, originalRow } of computedEdits) {
        if (!edit) {
          continue;
        }

        if (isEditAtOriginalBaseline({ edit, originalRow })) {
          if (filteredPriceEditMap[key]) {
            keysToRevert.push(key);
          }
          continue;
        }

        // Floor settings are table-wide when the caller passes them, otherwise
        // per row (a pair selection spans items with differing floors).
        const floorEnabled = priceFloorEnabled ?? originalRow.priceFloorEnabled;
        const floorMode =
          priceFloorEnabled === undefined
            ? originalRow.priceFloorMode
            : priceFloorMode;

        // Invalid rows are skipped and surface a toast so the user can see
        // which rows weren't included. Valid rows still apply as a single batch
        // to preserve undo grouping.
        if (floorEnabled && Boolean(floorMode)) {
          const validation = validatePricingFloor({
            costInMicros: originalRow.costInMicros,
            currencyCode,
            grossMarginPercentage: edit.grossMarginPercentage,
            inputType: edit.updatedField,
            priceInMicros: edit.priceInMicros,
          });
          if (!validation.valid) {
            showToast({
              title: i18n.t('pricing.bulk_edit.skipped_invalid_row', {
                reason: validation.message,
                restaurantName: toastNameOf(pivot, originalRow),
              }),
              variant: 'error',
            });
            continue;
          }
        }

        editsToApply.push(edit);
      }

      if (!editsToApply.length && !keysToRevert.length) {
        return false;
      }

      if (editsToApply.length) {
        applyEdits(editsToApply, scope);
      }

      if (keysToRevert.length) {
        revertKeys(keysToRevert);
      }

      return true;
    },
    [
      applyEdits,
      currencyCode,
      filteredPriceEditMap,
      pivot,
      priceFloorEnabled,
      priceFloorMode,
      revertKeys,
      scope,
      showToast,
    ],
  );

  const applyBulkInput = useCallback(() => {
    if (parsedBulkInputValue === null || selectedRowsForBulkEdit.length === 0) {
      return;
    }

    const hasAppliedEdits = applyBulkComputedEdits(
      selectedRowsForBulkEdit.map(({ effectiveRow, key, originalRow }) => {
        const { restaurantUUID, variantPackUUID } = originalRow;
        return {
          edit:
            bulkEditMode === BulkEditMode.Add
              ? computeAddAmountEdit({
                  delta: parsedBulkInputValue,
                  effectiveRow,
                  field: bulkEditField,
                  originalRow,
                  restaurantUUID,
                  variantPackUUID,
                })
              : computeExplicitValueEdit({
                  field: bulkEditField,
                  originalRow,
                  restaurantUUID,
                  value: parsedBulkInputValue,
                  variantPackUUID,
                }),
          key,
          originalRow,
        };
      }),
    );

    if (hasAppliedEdits) {
      trackBulkEdit(`${bulkEditMode}_${bulkEditField}`, [...selectedKeys]);
      setBulkInputValue('0');
    }
  }, [
    applyBulkComputedEdits,
    bulkEditField,
    bulkEditMode,
    parsedBulkInputValue,
    selectedKeys,
    selectedRowsForBulkEdit,
    trackBulkEdit,
  ]);

  const applyBulkQuickAction = useCallback(
    (type: PriceEditActionType) => {
      if (!selectedRowsForBulkEdit.length) {
        return;
      }

      applyBulkComputedEdits(
        selectedRowsForBulkEdit.map(({ effectiveRow, key, originalRow }) => ({
          edit: computeActionEdit({
            effectiveRow,
            originalRow,
            restaurantUUID: originalRow.restaurantUUID,
            type,
            variantPackUUID: originalRow.variantPackUUID,
          }),
          key,
          originalRow,
        })),
      );

      trackBulkEdit(type, [...selectedKeys]);
    },
    [
      applyBulkComputedEdits,
      selectedKeys,
      selectedRowsForBulkEdit,
      trackBulkEdit,
    ],
  );

  const revertBulkSelection = useCallback(() => {
    if (selectedEditedKeys.length === 0) {
      return;
    }

    revertKeys(selectedEditedKeys);
    trackBulkReversion(selectedEditedKeys);
    setBulkInputValue('0');
  }, [revertKeys, selectedEditedKeys, trackBulkReversion]);

  const bulkQuickActionStates = useMemo<BulkQuickActionState[]>(() => {
    const disabled = selectedRowsForBulkEdit.length === 0;
    const disableRevert = selectedEditedKeys.length === 0;
    const revertAction: BulkQuickActionState = {
      disabled: disableRevert,
      id: RevertEditAction.RevertEdit,
      onSelect: revertBulkSelection,
    };

    if (bulkEditField === PricingEditField.GrossMarginPercentage) {
      return [
        {
          disabled,
          id: GrossMarginEditAction.AddOnePercent,
          onSelect: () =>
            applyBulkQuickAction(GrossMarginEditAction.AddOnePercent),
        },
        {
          disabled,
          id: GrossMarginEditAction.SubtractOnePercent,
          onSelect: () =>
            applyBulkQuickAction(GrossMarginEditAction.SubtractOnePercent),
        },
        {
          disabled,
          id: GrossMarginEditAction.SetLastMargin,
          onSelect: () =>
            applyBulkQuickAction(GrossMarginEditAction.SetLastMargin),
        },
        revertAction,
      ];
    }

    return [
      {
        disabled,
        id: PriceEditAction.EndInX9,
        onSelect: () => applyBulkQuickAction(PriceEditAction.EndInX9),
      },
      {
        disabled,
        id: PriceEditAction.RandomRoundUp,
        onSelect: () => applyBulkQuickAction(PriceEditAction.RandomRoundUp),
      },
      {
        disabled,
        id: PriceEditAction.SetLastPrice,
        onSelect: () => applyBulkQuickAction(PriceEditAction.SetLastPrice),
      },
      revertAction,
    ];
  }, [
    applyBulkQuickAction,
    bulkEditField,
    revertBulkSelection,
    selectedEditedKeys.length,
    selectedRowsForBulkEdit.length,
  ]);

  const canApplyBulkInput =
    parsedBulkInputValue !== null && selectedRowsForBulkEdit.length > 0;

  return {
    applyBulkInput,
    applyBulkQuickAction,
    bulkEditField,
    bulkEditMode,
    bulkInputValue,
    bulkQuickActionStates,
    canApplyBulkInput,
    revertBulkSelection,
    setBulkEditField,
    setBulkEditMode,
    setBulkInputValue,
  };
};
